import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input } from "@resideo/blueprint-oneportal";
import { Spinner } from "@resideo/blueprint-react";
import { APIProvider } from "@vis.gl/react-google-maps";
import {
  AddressAutocompleteInput,
  type AutocompletedAddress,
} from "@/components/AddressAutocomplete";
import {
  AddressVerificationModal,
  type AddressVerificationItem,
} from "@/components/AddressVerificationModal";
import { verifyAddress, type Coordinates } from "@/utils/geocoding";
import type { ParsedAddress } from "@/utils/address";
import {
  COUNTRIES,
  useCountryStates,
  type CountryStateOption,
} from "@/hooks/useCountryStates";
import {
  fetchLocations,
  updateLocation,
  type ManagedLocation,
} from "@/pages/ManageLocations/manageLocationsService";
import styles from "./LocationDetailsStep.module.css";

export interface LocationDetailsStepProps {
  onBack: () => void;
  onFinish: () => void | Promise<void>;
  locationId?: string;
  embedded?: boolean;
  doneOnly?: boolean;
}

interface LocationFormValues {
  locationName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string;
}

const toFormValues = (location: ManagedLocation): LocationFormValues => ({
  locationName: location.name ?? "",
  addressLine1: location.address.addressLine1 ?? "",
  addressLine2: location.address.addressLine2 ?? "",
  city: location.address.city ?? "",
  state: location.address.stateProvinceRegionCode ?? "",
  zip: location.address.zipPostalCode ?? "",
  countryCode: location.address.countryCode ?? "US",
});

interface EditableLocation {
  location: ManagedLocation;
  values: LocationFormValues;
  /** A Places suggestion is already a verified address, so it skips geocoding. */
  placesSelected: boolean;
}

const addressKey = (...parts: Array<string | undefined>): string =>
  parts.map((part) => (part ?? "").trim().toLowerCase()).join("|");

/** Coordinates follow the address, so they only change when the saved address differs from the loaded one. */
const resolveGeoCoordinate = (
  entry: EditableLocation,
  address: ParsedAddress,
  geocoded?: Coordinates,
) => {
  const { address: original, geoCoordinate: current } = entry.location;
  const changed =
    addressKey(address.addressLine1, address.city, address.state, address.zip, address.countryCode) !==
    addressKey(
      original.addressLine1,
      original.city,
      original.stateProvinceRegionCode,
      original.zipPostalCode,
      original.countryCode,
    );

  return geocoded && changed
    ? { ...geocoded, elevation: current?.elevation ?? 0 }
    : current;
};

type RequiredLocationField = Exclude<keyof LocationFormValues, "addressLine2">;

const REQUIRED_FIELDS: RequiredLocationField[] = [
  "locationName",
  "addressLine1",
  "city",
  "state",
  "zip",
  "countryCode",
];

const REQUIRED_MESSAGE_KEYS: Record<RequiredLocationField, string> = {
  locationName: "onboardingTour.locationStep.locationNameRequired",
  addressLine1: "onboardingTour.locationStep.addressRequired",
  city: "onboardingTour.locationStep.cityRequired",
  state: "onboardingTour.locationStep.stateRequired",
  zip: "onboardingTour.locationStep.zipRequired",
  countryCode: "onboardingTour.locationStep.countryRequired",
};

const ADDRESS_FIELDS: (keyof LocationFormValues)[] = [
  "addressLine1",
  "addressLine2",
  "city",
  "state",
  "zip",
  "countryCode",
];

const normalizeValues = (values: LocationFormValues): LocationFormValues => ({
  locationName: values.locationName.trim(),
  addressLine1: values.addressLine1.trim(),
  addressLine2: values.addressLine2.trim(),
  city: values.city.trim(),
  state: values.state.trim(),
  zip: values.zip.trim(),
  countryCode: values.countryCode.trim(),
});

const isRequiredFieldMissing = (
  values: LocationFormValues,
  field: RequiredLocationField,
): boolean => !values[field].trim();

const toAddressFields = (values: LocationFormValues): ParsedAddress => ({
  addressLine1: values.addressLine1,
  addressLine2: values.addressLine2,
  city: values.city,
  state: values.state,
  zip: values.zip,
  countryCode: values.countryCode,
});

// A stored country code may predate Blueprint's list; keep it selectable rather than silently resetting it.
const withCurrentOption = (
  options: CountryStateOption[],
  value: string,
): CountryStateOption[] =>
  !value || options.some((option) => option.code === value)
    ? options
    : [...options, { code: value, name: value }];

interface LocationFieldsProps {
  index: number;
  values: LocationFormValues;
  disabled: boolean;
  onFieldChange: (field: keyof LocationFormValues, value: string) => void;
  onAddressSelect: (address: AutocompletedAddress) => void;
  getRequiredMessage: (field: RequiredLocationField) => string;
}

const LocationFields: React.FC<LocationFieldsProps> = ({
  index,
  values,
  disabled,
  onFieldChange,
  onAddressSelect,
  getRequiredMessage,
}) => {
  const { t } = useTranslation();
  const { states, isLoading: isLoadingStates } = useCountryStates(
    values.countryCode,
  );

  // A region code that isn't valid for the chosen country is blanked, forcing a reselection.
  useEffect(() => {
    if (!values.state || isLoadingStates || states.length === 0) return;
    if (states.some((option) => option.code === values.state)) return;
    onFieldChange("state", "");
  }, [states, isLoadingStates, values.state, onFieldChange]);

  const handleChange =
    (field: keyof LocationFormValues) =>
      (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        onFieldChange(field, event.target.value);

  const renderError = (field: RequiredLocationField) =>
    isRequiredFieldMissing(values, field) && (
      <span className={styles.fieldError}>{getRequiredMessage(field)}</span>
    );

  return (
    <>
      <div className={styles.field}>
        <label
          className={styles.label}
          htmlFor={`onboarding-location-name-${index}`}
        >
          {`${t("onboardingTour.locationStep.locationName")} *`}
        </label>
        <Input
          id={`onboarding-location-name-${index}`}
          name={`onboarding-location-name-${index}`}
          type="text"
          autoFocus={index === 0}
          className={styles.input}
          value={values.locationName}
          onChange={handleChange("locationName")}
          disabled={disabled}
          aria-invalid={isRequiredFieldMissing(values, "locationName")}
        />
        {renderError("locationName")}
      </div>

      <div className={styles.field}>
        <label
          className={styles.label}
          htmlFor={`onboarding-address-1-${index}`}
        >
          {`${t("onboardingTour.locationStep.addressLine1")} *`}
        </label>
        <AddressAutocompleteInput
          id={`onboarding-address-1-${index}`}
          name={`onboarding-address-1-${index}`}
          type="text"
          className={styles.input}
          aria-label={`${t("onboardingTour.locationStep.addressLine1")} *`}
          value={values.addressLine1}
          onChange={handleChange("addressLine1")}
          onAddressSelect={onAddressSelect}
          disabled={disabled}
          aria-invalid={isRequiredFieldMissing(values, "addressLine1")}
        />
        {renderError("addressLine1")}
      </div>

      <div className={styles.field}>
        <label
          className={styles.label}
          htmlFor={`onboarding-address-2-${index}`}
        >
          {`${t("onboardingTour.locationStep.addressLine2")} (optional)`}
        </label>
        <Input
          id={`onboarding-address-2-${index}`}
          name={`onboarding-address-2-${index}`}
          type="text"
          className={styles.input}
          value={values.addressLine2}
          onChange={handleChange("addressLine2")}
          disabled={disabled}
        />
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`onboarding-city-${index}`}>
            {`${t("onboardingTour.locationStep.city")} *`}
          </label>
          <Input
            id={`onboarding-city-${index}`}
            name={`onboarding-city-${index}`}
            type="text"
            className={styles.input}
            value={values.city}
            onChange={handleChange("city")}
            disabled={disabled}
            aria-invalid={isRequiredFieldMissing(values, "city")}
          />
          {renderError("city")}
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`onboarding-state-${index}`}>
            {`${t("onboardingTour.locationStep.state")} *`}
          </label>
          {states.length > 0 || isLoadingStates ? (
            <select
              id={`onboarding-state-${index}`}
              name={`onboarding-state-${index}`}
              className={styles.select}
              value={values.state}
              onChange={handleChange("state")}
              disabled={disabled || isLoadingStates}
              aria-invalid={isRequiredFieldMissing(values, "state")}
            >
              <option value="">
                {t("onboardingTour.locationStep.selectPlaceholder")}
              </option>
              {states.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </select>
          ) : (
            <Input
              id={`onboarding-state-${index}`}
              name={`onboarding-state-${index}`}
              type="text"
              className={styles.input}
              value={values.state}
              onChange={handleChange("state")}
              disabled={disabled}
              aria-invalid={isRequiredFieldMissing(values, "state")}
            />
          )}
          {renderError("state")}
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`onboarding-zip-${index}`}>
            {`${t("onboardingTour.locationStep.zip")} *`}
          </label>
          <Input
            id={`onboarding-zip-${index}`}
            name={`onboarding-zip-${index}`}
            type="text"
            className={styles.input}
            value={values.zip}
            onChange={handleChange("zip")}
            disabled={disabled}
            aria-invalid={isRequiredFieldMissing(values, "zip")}
          />
          {renderError("zip")}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor={`onboarding-country-${index}`}>
          {`${t("onboardingTour.locationStep.country")} *`}
        </label>
        <select
          id={`onboarding-country-${index}`}
          name={`onboarding-country-${index}`}
          className={styles.select}
          value={values.countryCode}
          onChange={handleChange("countryCode")}
          disabled={disabled}
          aria-invalid={isRequiredFieldMissing(values, "countryCode")}
        >
          <option value="">
            {t("onboardingTour.locationStep.selectPlaceholder")}
          </option>
          {withCurrentOption(COUNTRIES, values.countryCode).map((option) => (
            <option key={option.code} value={option.code}>
              {option.name}
            </option>
          ))}
        </select>
        {renderError("countryCode")}
      </div>
    </>
  );
};

/**
 * Final onboarding step: lets the user confirm/edit their first location's
 * details before finishing the tour.
 */
export const LocationDetailsStep: React.FC<LocationDetailsStepProps> = ({
  onBack,
  onFinish,
  locationId,
  embedded = false,
  doneOnly = false,
}) => {
  const { t } = useTranslation();
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const [locationForms, setLocationForms] = useState<EditableLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationItems, setVerificationItems] = useState<
    AddressVerificationItem[] | null
  >(null);
  const translate = useRef(t);
  // Geocoder results from the last Finish; kept across the verification modal until the save runs.
  const geocodedCoordinates = useRef<Record<string, Coordinates>>({});

  useEffect(() => {
    translate.current = t;
  }, [t]);

  useEffect(() => {
    let cancelled = false;

    const loadLocations = async () => {
      try {
        const locations = await fetchLocations({ includeHealth: false });

        if (cancelled) return;

        setLocationForms(
          locations
            // Read-only locations can't be updated; including them would fail the whole save.
            .filter((location) => location.canEdit !== false)
            .filter((location) => !locationId || location.id === locationId)
            .map((location) => ({
              location,
              values: toFormValues(location),
              placesSelected: false,
            })),
        );
      } catch {
        if (!cancelled) {
          setError(translate.current("onboardingTour.locationStep.loadFailed"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadLocations();

    return () => {
      cancelled = true;
    };
    // Reloading on a new `t` identity would discard whatever the user has typed.
  }, [locationId]);

  const updateEntry = (
    index: number,
    patch: (entry: EditableLocation) => EditableLocation,
  ) => {
    setLocationForms((prev) =>
      prev.map((item, currentIndex) =>
        currentIndex === index ? patch(item) : item,
      ),
    );
  };

  const handleFieldChange =
    (index: number) => (field: keyof LocationFormValues, value: string) =>
      updateEntry(index, (entry) => {
        if (entry.values[field] === value) return entry;

        if (field === "countryCode") {
          // The previously picked region no longer belongs to the selected country.
          return {
            ...entry,
            values: { ...entry.values, countryCode: value, state: "" },
            placesSelected: false,
          };
        }

        return {
          ...entry,
          values: { ...entry.values, [field]: value },
          placesSelected: ADDRESS_FIELDS.includes(field)
            ? false
            : entry.placesSelected,
        };
      });

  // Places supplies country and region together, so this must stay a single atomic update.
  const handleAddressSelect =
    (index: number) => (address: AutocompletedAddress) =>
      updateEntry(index, (entry) => ({
        ...entry,
        placesSelected: true,
        values: {
          ...entry.values,
          addressLine1: address.addressLine1 || entry.values.addressLine1,
          addressLine2: address.addressLine2 || entry.values.addressLine2,
          city: address.city || entry.values.city,
          state: address.state || entry.values.state,
          zip: address.zip || entry.values.zip,
          countryCode: address.countryCode || entry.values.countryCode,
        },
      }));

  const persistLocations = async (
    resolved: Record<string, ParsedAddress> = {},
  ) => {
    setIsSaving(true);
    setError(null);

    try {
      await Promise.all(
        locationForms.map((entry) => {
          const values = normalizeValues(entry.values);
          const address = resolved[entry.location.id] ?? toAddressFields(values);

          return updateLocation({
            consumerAccountId: entry.location.consumerAccountId,
            locationId: entry.location.id,
            name: values.locationName,
            address: {
              addressLine1: address.addressLine1,
              addressLine2: address.addressLine2,
              city: address.city,
              stateProvinceRegionCode: address.state,
              zipPostalCode: address.zip,
              countryCode: address.countryCode || "US",
            },
            geoCoordinate: resolveGeoCoordinate(
              entry,
              address,
              geocodedCoordinates.current[entry.location.id],
            ),
            consumerDeviceIds: entry.location.consumerDeviceIds,
            isConfirmed: true,
          });
        }),
      );
      // Keep the spinner up while the parent persists onboarding state and navigates.
      await onFinish();
    } catch {
      setError(t("onboardingTour.locationStep.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinish = async () => {
    if (locationForms.length === 0) {
      await persistLocations();
      return;
    }

    // Without Maps there is nothing to verify against, so don't block on a suggestion-less prompt.
    if (!googleMapsApiKey) {
      await persistLocations();
      return;
    }

    setIsSaving(true);
    setError(null);
    geocodedCoordinates.current = {};

    const checks = await Promise.all(
      locationForms.map(async (entry) => {
        const original = toAddressFields(normalizeValues(entry.values));
        const { isVerified, suggestedAddress, coordinates } =
          await verifyAddress(original);

        if (coordinates) {
          geocodedCoordinates.current[entry.location.id] = coordinates;
        }

        // A Places suggestion is already a verified address, so it never needs confirming.
        return isVerified || entry.placesSelected
          ? null
          : {
            locationId: entry.location.id,
            original,
            suggested: suggestedAddress,
          };
      }),
    );

    const unverified = checks.filter(
      (item): item is AddressVerificationItem => item !== null,
    );

    if (unverified.length > 0) {
      setIsSaving(false);
      setVerificationItems(unverified);
      return;
    }

    await persistLocations();
  };

  const hasValidationErrors = locationForms.some(({ values }) =>
    REQUIRED_FIELDS.some((field) => isRequiredFieldMissing(values, field)),
  );
  const getRequiredMessage = (field: RequiredLocationField): string =>
    t(REQUIRED_MESSAGE_KEYS[field]);

  const content = (
    <div
      className={embedded ? styles.embedded : styles.overlay}
      role={embedded ? undefined : "dialog"}
      aria-modal={embedded ? undefined : "true"}
      aria-labelledby={embedded ? undefined : "location-details-title"}
      data-testid="onboarding-location-step"
    >
      <div className={embedded ? styles.embeddedContent : styles.content}>
        <h2 id="location-details-title" className={styles.title}>
          {t("onboardingTour.locationStep.title")}
        </h2>
        <p className={styles.subtitle}>
          {t("onboardingTour.locationStep.subtitle")}
        </p>

        {error && <p role="alert">{error}</p>}

        {isLoading ? (
          <div
            className={styles.loadingContainer}
            role="status"
            aria-label="Loading location details"
            data-testid="onboarding-location-loading"
          >
            <Spinner size="large" />
          </div>
        ) : locationForms.map((entry, index) => (
          <section key={entry.location.id} className={styles.locationSection}>
            {index > 0 && <hr className={styles.sectionDivider} />}

            <LocationFields
              index={index}
              values={entry.values}
              disabled={isLoading || isSaving}
              onFieldChange={handleFieldChange(index)}
              onAddressSelect={handleAddressSelect(index)}
              getRequiredMessage={getRequiredMessage}
            />
          </section>
        ))}

        <div className={styles.actions}>
          {!doneOnly && (
            <Button
              type="button"
              variant="highlightTertiary"
              className={styles.backButton}
              onClick={onBack}
              disabled={isSaving}
              data-testid="onboarding-location-back"
            >
              {t("common.back")}
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            className={`${styles.finishButton} ${doneOnly ? styles.doneButton : ""}`}
            onClick={handleFinish}
            disabled={
              isLoading ||
              isSaving ||
              (Boolean(error) && locationForms.length === 0) ||
              hasValidationErrors
            }
            data-testid="onboarding-location-finish"
          >
            {doneOnly ? t("locationGuidance.done") : t("onboardingTour.locationStep.finish")}
          </Button>
        </div>
      </div>

      {isSaving && (
        <div
          className={styles.savingOverlay}
          role="status"
          aria-label={t("common.saving")}
          data-testid="onboarding-location-saving"
        >
          <Spinner size="large" />
        </div>
      )}

      {verificationItems && (
        <AddressVerificationModal
          items={verificationItems}
          onCancel={() => setVerificationItems(null)}
          onComplete={(resolved) => {
            setVerificationItems(null);
            void persistLocations(resolved);
          }}
        />
      )}
    </div>
  );

  if (!googleMapsApiKey) {
    return content;
  }

  return (
    <APIProvider apiKey={googleMapsApiKey} libraries={["places"]}>
      {content}
    </APIProvider>
  );
};

export default LocationDetailsStep;
